/**
 * DiscountEditor - Wave-style discount editor for orders
 * 
 * Provides a clean interface for adding/editing discounts with:
 * - Type selector (PERCENT or AMOUNT)
 * - Value input with real-time validation
 * - Calculated discount amount display
 * - Lock state when invoice is issued
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Loader2,
  Percent,
  DollarSign,
  Edit2,
  X,
  Lock,
  Tag,
} from 'lucide-react';

const DiscountEditor = ({ 
  orderId,
  subtotal = 0,
  discountConfig = null, // { type: "PERCENT"|"AMOUNT", value: number }
  discountAmount = 0,
  discountDescription = '', // Description text for the discount
  isLocked = false,
  onSave, // async (type, value, description) => Promise<result>
  onUpdate, // callback to refresh order data
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [discountType, setDiscountType] = useState(discountConfig?.type || 'AMOUNT');
  const [discountValue, setDiscountValue] = useState(discountConfig?.value || 0);
  const [description, setDescription] = useState(discountDescription || '');
  
  // Reset form when discountConfig changes
  useEffect(() => {
    setDiscountType(discountConfig?.type || 'AMOUNT');
    setDiscountValue(discountConfig?.value || 0);
    setDescription(discountDescription || '');
  }, [discountConfig, discountDescription]);
  
  // Calculate preview discount amount
  const previewDiscountAmount = () => {
    if (discountValue <= 0) return 0;
    if (discountType === 'PERCENT') {
      const pct = Math.min(100, Math.max(0, discountValue));
      return (subtotal * (pct / 100)).toFixed(2);
    }
    return Math.min(discountValue, subtotal).toFixed(2);
  };
  
  const handleOpen = () => {
    if (isLocked) return;
    setDiscountType(discountConfig?.type || 'AMOUNT');
    setDiscountValue(discountConfig?.value || 0);
    setDescription(discountDescription || '');
    setError(null);
    setIsEditing(true);
  };
  
  const handleSave = async () => {
    setError(null);
    
    // Validate
    if (discountValue < 0) {
      setError('Discount value cannot be negative');
      return;
    }
    
    if (discountType === 'PERCENT' && discountValue > 100) {
      setError('Percentage discount cannot exceed 100%');
      return;
    }
    
    setSaving(true);
    try {
      await onSave(discountType, discountValue, description);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to save discount');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRemove = async () => {
    setSaving(true);
    try {
      await onSave('AMOUNT', 0);
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      setError(err.message || 'Failed to remove discount');
    } finally {
      setSaving(false);
    }
  };
  
  // Display the current discount state
  const hasDiscount = discountConfig && discountConfig.value > 0;
  
  return (
    <>
      {/* Discount Display Row */}
      <div className="flex justify-between text-sm items-center" data-testid="discount-row">
        <div className="flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-gray-500">Discount</span>
          {discountDescription && hasDiscount && (
            <span className="text-xs text-green-600 italic">({discountDescription})</span>
          )}
          {isLocked && <Lock className="w-3 h-3 text-gray-400" />}
        </div>
        
        {isLocked ? (
          // Locked state - show discount info only
          <div className="flex items-center gap-2">
            {hasDiscount ? (
              <>
                <Badge variant="outline" className="text-xs">
                  {discountConfig.type === 'PERCENT' 
                    ? `${discountConfig.value}%` 
                    : `$${discountConfig.value.toFixed(2)}`}
                </Badge>
                <span className="text-green-600 font-medium">-${discountAmount.toFixed(2)}</span>
              </>
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        ) : (
          // Editable state
          <button
            onClick={handleOpen}
            className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1 rounded transition-colors"
            data-testid="edit-discount-btn"
          >
            {hasDiscount ? (
              <>
                <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                  {discountConfig.type === 'PERCENT' 
                    ? `${discountConfig.value}%` 
                    : `$${discountConfig.value.toFixed(2)}`}
                </Badge>
                <span className="text-green-600 font-medium">-${discountAmount.toFixed(2)}</span>
                <Edit2 className="w-3 h-3 text-gray-400" />
              </>
            ) : (
              <>
                <span className="text-blue-600">+ Add discount</span>
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Edit Modal */}
      <Dialog open={isEditing} onOpenChange={setIsEditing}>
        <DialogContent className="sm:max-w-md" data-testid="discount-editor-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5" />
              {hasDiscount ? 'Edit Discount' : 'Add Discount'}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Configure a percentage or flat amount discount for this order
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Discount Type Selector */}
            <div>
              <Label>Discount Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <button
                  type="button"
                  onClick={() => setDiscountType('PERCENT')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    discountType === 'PERCENT' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="discount-type-percent"
                >
                  <Percent className="w-4 h-4" />
                  <span className="font-medium">Percentage</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDiscountType('AMOUNT')}
                  className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    discountType === 'AMOUNT' 
                      ? 'border-blue-500 bg-blue-50 text-blue-700' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid="discount-type-amount"
                >
                  <DollarSign className="w-4 h-4" />
                  <span className="font-medium">Flat Amount</span>
                </button>
              </div>
            </div>
            
            {/* Discount Value Input */}
            <div>
              <Label>
                {discountType === 'PERCENT' ? 'Percentage (0-100%)' : 'Amount ($)'}
              </Label>
              <div className="relative mt-1">
                {discountType === 'PERCENT' ? (
                  <div className="relative">
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      className="pr-8 text-lg"
                      placeholder="0"
                      data-testid="discount-value-input"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                ) : (
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={discountValue}
                      onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                      className="pl-7 text-lg"
                      placeholder="0.00"
                      data-testid="discount-value-input"
                    />
                  </div>
                )}
              </div>
            </div>
            
            {/* Discount Description */}
            <div>
              <Label>Description (optional)</Label>
              <Input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="e.g., Volume Discount, Holiday Special, Early Payment"
                className="mt-1"
                data-testid="discount-description-input"
              />
              <p className="text-xs text-gray-500 mt-1">Shown on invoice/quote PDFs</p>
            </div>
            
            {/* Preview Calculation */}
            {discountValue > 0 && (
              <div className="p-3 bg-gray-50 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="font-medium">${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">
                    Discount ({discountType === 'PERCENT' ? `${discountValue}%` : `$${discountValue.toFixed(2)}`})
                  </span>
                  <span className="text-green-600 font-medium">-${previewDiscountAmount()}</span>
                </div>
                <div className="flex justify-between text-sm font-semibold pt-2 border-t">
                  <span>After Discount</span>
                  <span>${(subtotal - parseFloat(previewDiscountAmount())).toFixed(2)}</span>
                </div>
              </div>
            )}
            
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}
          </div>
          
          <DialogFooter className="flex justify-between">
            <div>
              {hasDiscount && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleRemove}
                  disabled={saving}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid="remove-discount-btn"
                >
                  Remove Discount
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || discountValue < 0}
                data-testid="save-discount-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {hasDiscount ? 'Update Discount' : 'Add Discount'}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DiscountEditor;
